import Grid2 from '@mui/material/Unstable_Grid2';
import { Fragment } from 'react';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import type { ImageTextStackedXup } from '@/components/HighlightImageTextStacked/HighlightImageTextStacked';
import useSharedStyles from '@/components/HighlightImageTextStacked/SharedStackedStyles.styles';
import { getImages } from '@/components/HighlightImageTextStacked/utils';
import type { VideoTextStacked } from '@/components/HighlightVideoInline/HighlightVideoInline';
import ArtDirectedImage, {
  type ImageSources,
} from '@/components/Image/ArtDirectedImage';
import VimeoVideo from '@/components/VimeoVideo/VimeoVideo';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './SingleStack.styles';

export type SingleStackProps = {
  content: ImageTextStackedXup | VideoTextStacked;
  isOnDark: boolean;
};

const SingleStack = ({ content, isOnDark }: SingleStackProps) => {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes: sharedClasses } = useSharedStyles({ isOnDark });
  const { classes, cx } = useStyles();
  const stack = content?.fields;

  const { isSmall, isExtraSmall } = useViewPortSize();
  const isMobile = isSmall || isExtraSmall;
  let images: ImageSources | undefined;
  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let video;

  if ('backgroundImages' in stack) {
    images = stack?.backgroundImages
      ? getImages(stack?.backgroundImages)
      : undefined;
  }

  if ('videos' in stack) {
    if ((isSmall || isExtraSmall) && stack?.videos?.[1]) {
      video = stack?.videos?.[1];
    } else {
      video = stack?.videos?.[0];
    }
  }

  return (
    <Fragment>
      <Grid2 xs={12}>
        {images && (
          <ArtDirectedImage
            images={images}
            alt=''
            role='presentation'
            className={cx(
              sharedClasses.image,
              sharedClasses.media,
              classes.singleImage
            )}
          />
        )}
        {video && !images && (
          <div className={sharedClasses.media}>
            <VimeoVideo controls url={video} isRounded />
          </div>
        )}
      </Grid2>
      <Grid2 className={classes.stackText} lg={6} md={8} sm={8} xs={12}>
        {stack?.subhead && (
          <h4 className={cx(globalClasses.h4, sharedClasses.subhead)}>
            {stack.subhead}
          </h4>
        )}
        {stack?.body && (
          <div className={cx(globalClasses.body, sharedClasses.body)}>
            {stack.body}
          </div>
        )}
      </Grid2>
      <Grid2 lg={6} md={4} xs={12}>
        {stack?.cta && (
          <div
            className={classes.ctaContainer}
            style={{ justifyContent: isMobile ? 'start' : 'end' }}
          >
            <Button
              isOnDark={isOnDark}
              variant={ButtonVariant.SECONDARY}
              label={stack?.cta?.fields?.label}
              href={stack?.cta?.fields?.url}
              className={sharedClasses.cta}
            />
          </div>
        )}
      </Grid2>
    </Fragment>
  );
};

export default SingleStack;
